import { toast } from 'react-toastify';
import { Dispatch } from 'redux';
import XLSX from 'xlsx';
import { cloneDeep, isEqual, round, sortBy } from 'lodash';
import { zxcvbn, ZxcvbnOptions } from '@zxcvbn-ts/core';
import { URLSearchParamsInit, useLocation } from 'react-router-dom';
import { Row } from 'react-table';
import { IWarning } from '@praktio/common-ui';
import type { ParentToLearnerMessage } from '@praktio/common-ui';

import { getZxcvbnOptions } from './api';
import { updateAuthenticated } from '../ducks/auth';
import { DateFilterType } from './enums';
import {
  FormValidationErrors,
  MessageTypes,
  None,
  Roles,
  Jurisdictions,
  TimezoneOffsetMs,
  BLANKS_FILTER,
} from './constants';

interface Errors extends Error {
  message: string;
  response: {
    status: number;
    data: {
      message: string;
    };
  };
}

export const processError = (e: any, dispatch: Dispatch) => {
  const error = e as Errors;
  let message = error.message;
  let errorCode: number | undefined;
  if (error.response) {
    if (error.response.status === 401) {
      dispatch(updateAuthenticated());
    }

    if (error.response.data) {
      if (error.response.data.message) {
        message = error.response.data.message;
        console.error(error.response.data);
      }

      errorCode = (error.response.data as any).errorCode;
    }
  }

  return { message, errorCode };
};

export const notifyError = (e: any, dispatch: Dispatch) => {
  const data = processError(e, dispatch);
  if (e?.response?.status !== 401) {
    toast.error(data.message || `An error has occurred`);
  }

  return data;
};

export const notifySuccess = (message: string, disableAutoClose?: boolean) => {
  toast.success(
    <div className="ml-2 mr-2 d-flex">
      <span className="mr-1 material-icons">check</span>
      <span>{message}</span>
    </div>,
    {
      autoClose: disableAutoClose ? false : 5000,
    },
  );
};

export const notifySaveSuccess = () => {
  notifySuccess('Save successful');
};

export const isPlatformAdmin = (user?: IUser) => {
  return !!(user?.roles || []).find((r) => r.role === Roles.PlatformAdmin);
};

interface ShouldShowFullOverlayArgs {
  checkingAuth: boolean;
  isUnauthedPath: boolean;
  showLogin: boolean;
}

export const shouldShowFullOverlay = ({
  checkingAuth,
  isUnauthedPath,
  showLogin,
}: ShouldShowFullOverlayArgs): boolean => checkingAuth && !isUnauthedPath && !showLogin;

export const isTeamManager = (user?: IUser) => {
  return !!(user?.roles || []).find((r) => r.role === Roles.TeamManager) || isPlatformAdmin(user);
};

export const hasTeamManagerRole = (user: Optional<IUser>) => {
  if (!user) {
    return false;
  }

  const notPlatformManage = user.roles?.every((role) => role.role !== Roles.PlatformAdmin);
  const isTeamManager = user.roles?.some((role) => role.role === Roles.TeamManager);

  return notPlatformManage && isTeamManager;
};

export const getRoleName = (role: string) => {
  switch (role) {
    case Roles.PlatformAdmin:
      return 'Platform Admin';
    case Roles.PlatformDelegate:
      return 'Platform Delegate';
    case Roles.TenantAdmin:
      return 'Tenant Admin';
    case Roles.TeamManager:
      return 'Team Manager';
    case Roles.Learner:
      return 'Learner';
    default:
      return;
  }
};

export const buildBulkTableData = (
  headers: IHeader[],
  fileBuffer: ArrayBuffer,
  teams?: ITeam[],
): { data: any[]; valid: boolean } => {
  const data = new Uint8Array(fileBuffer);
  const wb = XLSX.read(data, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const sheetData = XLSX.utils.sheet_to_json(ws, { header: 1 });
  const updatedTableData = [];
  let updatedAllValid = true;
  const emails: { [key: string]: any[] } = {};
  for (let r = 1; r < sheetData.length; r += 1) {
    const row = sheetData[r] as any[];
    const tableRow: any = { invalidCells: {} };
    for (let h = 0; h < headers.length; h += 1) {
      const header = headers[h];
      const { accessor } = header;
      let value = row[h];
      if (value?.trim) {
        value = value.trim();
      }

      if (accessor && (value || value === 0)) {
        tableRow[accessor] = value;
        if (accessor.startsWith('team')) {
          const team = teams?.find((t) => t._id === value);
          if (!team) {
            tableRow.hasError = true;
            updatedAllValid = false;
          } else {
            tableRow[`${accessor}id`] = value;
            tableRow[accessor] = team.name;
          }
        } else if (accessor.startsWith('cleState')) {
          const jurisdiction = Jurisdictions.find((s) => s === value);
          if (!jurisdiction) {
            tableRow.hasError = true;
            updatedAllValid = false;
          }
        } else if (accessor === 'noCle') {
          tableRow[accessor] = 'No CLE';
        } else if (accessor === 'clearCle') {
          tableRow[accessor] = "Clear 'No' CLE";
        }
      }
    }

    tableRow.email = tableRow.email?.replaceAll('’', `'`);
    tableRow.updateEmail = tableRow.updateEmail?.replaceAll('’', `'`);
    const email = tableRow.email.toLowerCase();
    if (email) {
      updatedTableData.push(tableRow);
      if (!emails[email]) {
        emails[email] = [];
      }

      emails[email].push(tableRow);

      if (emails[email].length > 1) {
        for (const row of emails[email]) {
          row.hasError = true;
          updatedAllValid = false;
          if (!row.errors) {
            row.errors = [];
          }

          row.errors.push('Duplicate email with another row. Check sheet.');
        }
      }
    }
  }

  return { data: updatedTableData, valid: updatedAllValid };
};

export const emptyString = (value?: string) => {
  return !value || value === '';
};

let loaded_zxcvbn = false;
const loadOptions = async () => {
  const response = await getZxcvbnOptions();
  ZxcvbnOptions.setOptions(response.data);
  loaded_zxcvbn = true;
};

export const passwordValidator = async (password: string) => {
  if (!loaded_zxcvbn) {
    await loadOptions();
  }

  if (!password || password === '') {
    return [];
  }

  const results = zxcvbn(password || '');
  const errors = [];
  if (results?.feedback?.warning && results?.feedback?.warning !== '') {
    errors.push(results.feedback?.warning);
  }

  if (results?.feedback?.suggestions?.length > 0) {
    errors.push(...results.feedback?.suggestions);
  }

  if (results.score < 3 && errors.length === 0) {
    errors.push('Password not strong enough');
  }

  return errors;
};

export const buildTeamHierarchy = ({
  teams,
  includeAll,
  rootId,
  sorted,
}: {
  teams?: ITeam[];
  includeAll?: boolean;
  rootId?: string;
  sorted: boolean;
}) => {
  const teamById: IKey = {};
  teams?.forEach((t) => {
    const team = cloneDeep(t);

    const isAncestorOnly = (t as any).isAncestorOnly;
    const hasNoReportableData = !isAncestorOnly && (!t.courses?.length || t.hasLearners === false);

    if (isAncestorOnly || hasNoReportableData) {
      team.disabled = true;
    }

    teamById[t._id] = team;
  });
  let topLevelNodes: any[] = [];
  const parents: any[] = [];
  Object.values(teamById).forEach((v) => {
    let parent;
    if (v.parent) {
      parent = teamById[v.parent];
      if (parent) {
        if (!parent.children) {
          parent.children = [];
          parents.push(parent);
        }

        parent.children.push(v);
      }
    }

    if ((!rootId && !parent) || (rootId && v.parent === rootId)) {
      topLevelNodes.push(v);
    }
  });

  if (includeAll) {
    topLevelNodes.forEach((t) => (t.parent = 'All'));
  }

  if (sorted) {
    topLevelNodes = sortBy(topLevelNodes, (n) => n?.name?.toLowerCase()?.trim());
    for (const parent of parents) {
      if (parent.children) {
        parent.children = sortBy(parent.children, (n) => n?.name?.toLowerCase()?.trim());
      }
    }
  }

  let hierarchy = includeAll
    ? [{ name: 'All', children: topLevelNodes, _id: 'All' }]
    : topLevelNodes;

  hierarchy = pruneDisabledSinglePathRoot(hierarchy);

  return hierarchy;
};

/**
 * Prunes only the top-level disabled ancestor from a team hierarchy.
 *
 * Rule:
 * - If exactly one top-level node exists, AND
 * - that node is disabled (not selectable), AND
 * - it has children,
 * → remove it and promote its children to the top level.
 *
 * Only prunes ONE level (the top-level parent). The user implicitly knows
 * their firm/organization, so showing it adds clutter. However, intermediate
 * ancestors (e.g., "2025 Fall") provide useful navigation context and are kept.
 *
 * When user has access to multiple top-level teams (different families),
 * nodes.length > 1 so nothing is pruned, preserving the full hierarchy
 * to distinguish between families.
 */
const pruneDisabledSinglePathRoot = (nodes: any[]): any[] => {
  if (nodes.length === 1 && nodes[0].disabled && nodes[0].children?.length) {
    nodes = nodes[0].children;
  }

  return nodes;
};

export const getTeamAncestorPath = (teams: ITeam[], teamId: string): ITeam[] => {
  const teamMap = new Map(teams.map((t) => [t._id, t]));
  const path: ITeam[] = [];
  let current = teamMap.get(teamId);

  while (current) {
    path.unshift(current);
    current = current.parent ? teamMap.get(current.parent) : undefined;
  }

  return path;
};

export const flattenTeamChildren = (
  teamId: string,
  team: ITeam,
  teams: ITeam[],
  children: ITeam[],
) => {
  if (team._id !== teamId) {
    children.push(team);
  }

  teams
    .filter((t) => t.parent === team._id)
    .forEach((t) => {
      flattenTeamChildren(teamId, t, teams, children);
    });
};

const iframeOrigin = process.env.REACT_APP_IFRAME_ORIGIN || window.origin;

export const setAuthorShowToC = (ref: React.RefObject<HTMLIFrameElement>, value: boolean) => {
  if (ref?.current?.contentWindow) {
    ref.current.contentWindow.postMessage(
      { type: MessageTypes.ToC, data: value } satisfies ParentToLearnerMessage,
      iframeOrigin,
    );
  }
};

export const setAuthorSlideId = (ref: React.RefObject<HTMLIFrameElement>, value: string) => {
  if (ref?.current?.contentWindow) {
    ref.current.contentWindow.postMessage(
      { type: MessageTypes.Slide, data: { _id: value } } satisfies ParentToLearnerMessage,
      iframeOrigin,
    );
  }
};

export const setAuthorTitle = (ref: React.RefObject<HTMLIFrameElement>, value?: string) => {
  if (ref?.current?.contentWindow) {
    ref.current.contentWindow.postMessage(
      { type: MessageTypes.Title, data: value } satisfies ParentToLearnerMessage,
      iframeOrigin,
    );
  }
};

// Presence "Activity" label for a page: on a course page, "Course › Module"
// (from the URL's moduleId + the loaded course); otherwise the top path segment.
// Shared so App (every heartbeat) and CourseViewer produce the same value.
export const getPresenceContext = (
  pathname: string,
  course?: { title?: string; modules?: { _id?: string; title?: string }[] },
): string => {
  const parts = pathname.split('/');
  if (parts[1] === 'courses' && parts[2]) {
    const moduleId = parts[3] === 'modules' ? parts[4] : undefined;
    const foundModule = course?.modules?.find((m) => m._id === moduleId);
    return [course?.title, foundModule?.title].filter(Boolean).join(' › ') || 'Course';
  }
  return parts[1] || 'Home';
};

interface IFormErrorProps {
  user: IUser;
  requirePassword?: boolean;
  ignoreCurrentPassword?: boolean;
  cleRequired?: boolean;
  isSelfSignup?: boolean;
  noCoursesSelected?: boolean;
}

export const findFormErrors = async ({
  user,
  requirePassword,
  ignoreCurrentPassword,
  cleRequired,
  isSelfSignup,
  noCoursesSelected,
}: IFormErrorProps) => {
  const {
    email,
    firstName,
    lastName,
    currentPassword,
    password,
    confirmPassword,
    optOutCLEs,
    cles,
    code,
    token,
    reportTeams,
  } = user;
  const newErrors: IBootstrapFormErrors = {};
  // Email Errors
  if (!email || email === '') {
    newErrors.email = FormValidationErrors.blank;
  }

  if (!isSelfSignup) {
    // First Name Errors
    if (!firstName || firstName === '') {
      newErrors.firstName = FormValidationErrors.blank;
    }
    // Last Name Errors
    if (!lastName || lastName === '') {
      newErrors.lastName = FormValidationErrors.blank;
    }

    // CLE Errors
    // If optOutCLE is NOT true and no values entered in Jurisdiction/Bar#
    if (!optOutCLEs && (cles || []).length < 1 && cleRequired) {
      newErrors.cles = FormValidationErrors.cles;
    }
  } else {
    // Code Errors
    if (!code || code === '') {
      newErrors.code = FormValidationErrors.blank;
    }

    if (!confirmPassword || confirmPassword === '') {
      newErrors.confirmPassword = FormValidationErrors.blank;
    }

    // Captcha Errors
    if (!token || token === '') {
      newErrors.token = FormValidationErrors.incompleteCaptcha;
    }
  }

  // --Password Errors--
  if (requirePassword && !password) {
    newErrors.password = FormValidationErrors.blank;
  }

  if (requirePassword && !confirmPassword) {
    newErrors.confirmPassword = FormValidationErrors.blank;
  }
  // Any value entered for updatePassword and/or confirmUpdatePassword, but they do not match.
  else if ((password || confirmPassword) && password !== confirmPassword) {
    newErrors.confirmPassword = FormValidationErrors.passwordsDoNotMatch;
  }

  // Any value for update or confirm password, but current password not entered
  // This only needs to be checked for non-admin users that are NOT going
  // through the welcome email w/ token. (ignoreCurrentPassword is set to true
  // when using a token)
  if (
    !ignoreCurrentPassword &&
    (requirePassword || password || confirmPassword) &&
    (!currentPassword || currentPassword === '')
  ) {
    newErrors.currentPassword = FormValidationErrors.emptyPassword;
  }

  // New password is not robust enough
  const newPasswordErrors = await passwordValidator(password || '');
  if (newPasswordErrors.length > 0) {
    newErrors.password = newPasswordErrors[0];
  }
  // New Password is the same as old password
  if (
    !ignoreCurrentPassword &&
    password &&
    password !== '' &&
    currentPassword &&
    password === currentPassword
  ) {
    newErrors.password = FormValidationErrors.notSamePassword;
  }

  // If the user is a team manager and has no report teams selected, show an error.
  const isTeamManager = hasTeamManagerRole(user);

  if (isTeamManager && reportTeams && reportTeams.length < 1) {
    newErrors.reportTeams = FormValidationErrors.noReportTeams;
  }

  if (isTeamManager && noCoursesSelected) {
    newErrors.reportDeselectedCourses = FormValidationErrors.noReportCourses;
  }

  return newErrors;
};

const updateCLEFields = (value: ICLEEntry, userToUpdate: IUser) => {
  const { state, barNumber } = value;
  // Only run the following if a) the jurisdiction has a value and either
  // b1) the bar number is undefined (this is for deleting a row) or
  // b2) the bar number is not an empty string (this is for adding a new valid row)
  if (state !== None && (!barNumber || barNumber !== '')) {
    // If the user currently does not have a value for cles,
    // make one and set it to an empty array.
    if (!userToUpdate.cles) {
      userToUpdate.cles = [];
    }

    // Find the index in cles of the jurisdiction that has been modified
    const index = userToUpdate.cles.findIndex((cleEntry) => cleEntry.state === state);

    // If that index exists (that jurisdiction was already present)...
    if (index > -1) {
      // If a bar number has been passed, update the entry for the passed jurisdiction
      // to the newly passed bar number.
      if (barNumber) {
        const entry = userToUpdate.cles[index];
        userToUpdate.cles.splice(index, 1, { _id: entry._id, state, barNumber });
        // If a bar number has not been passed, delete the entry for the
        // passed jurisdiction.
      } else {
        userToUpdate.cles.splice(index, 1);
      }
      // If that index doesn't exist (that jurisdiction was not present yet)
      // and there is a bar number present...
    } else if (barNumber) {
      // Add this new entry to the user's cles array.
      userToUpdate.cles.push({ state, barNumber });
    }

    // Since they have passed values, set optOut to false
    userToUpdate.optOutCLEs = false;
  }
};

export const updateUserFormFields = (
  field: string,
  value: UserFormValueOptions,
  userToUpdate?: IUser,
) => {
  if (!userToUpdate) {
    return;
  }

  if (field === 'cles' && value) {
    updateCLEFields(value as ICLEEntry, userToUpdate);
  } else {
    // If the value of the input becomes an empty string:
    // If a password field, remove the property from
    // the user object.
    // Otherwise, set the value to null to match the defaulted values on the backend
    if (value === '' && userToUpdate) {
      if (['password', 'currentPassword', 'confirmPassword'].includes(field)) {
        delete (userToUpdate as IKey)[field];
      } else {
        (userToUpdate as IKey)[field] = null;
      }
      // If the value of the field has a new non-empty string or boolean...
    } else {
      // If this is a report notification-related field...
      if (['frequency', 'day', 'date'].includes(field)) {
        // and the user does not have a reportNotification field yet, create one.
        if (userToUpdate && !userToUpdate.reportNotification) {
          userToUpdate.reportNotification = {};
        }

        // If the frequency is being set to none, delete the field.
        if (userToUpdate && field === 'frequency' && !value) {
          delete userToUpdate.reportNotification;
        } else if (userToUpdate?.reportNotification) {
          // Otherwise, set the field to the passed value.
          userToUpdate.reportNotification[field] = value;
        }
        // ...and the field is not report notification-related, set this field and vlaue in the user object.
      } else {
        (userToUpdate as IKey)[field] = value;
      }
    }
  }
};

export const updateFormErrors = (
  field: string,
  errorsToUpdate: IBootstrapFormErrors | undefined,
) => {
  // If switching "seekingCLE", target the "cles" field for error clearing
  if (field === 'optOutCLEs') {
    field = 'cles';
  }

  // If there is a 'passwords do not match' error on the form, we
  // need to clear it when the user modifies a password field.
  // It will return on submission if the user's passwords still do not match.
  if (field.toLowerCase().includes('password') && errorsToUpdate) {
    for (const [key, value] of Object.entries(errorsToUpdate)) {
      if (value === FormValidationErrors.passwordsDoNotMatch) {
        delete errorsToUpdate[key];
      }
    }
  }

  // When a user begins editing a field that had a form error created
  // upon submit, clear that form error.
  if (errorsToUpdate && !!errorsToUpdate[field]) {
    delete errorsToUpdate[field];
  }
};

export const updateWarningsList = (
  warningsToUpdate: IWarning[],
  clonedFormErrors: IBootstrapFormErrors | undefined,
) => {
  const numberOfFormErrors = Object.keys(clonedFormErrors || {});
  // When the number of displayed form errors becomes zero,
  // clear out any warnings that are a related to basic form errors
  // or an invalid password
  if (numberOfFormErrors.length === 0) {
    for (let i = 0; i < warningsToUpdate.length; i++) {
      const warningTitle = warningsToUpdate[i].title;
      if (warningTitle === 'Form Error' || warningTitle === 'Invalid Password') {
        warningsToUpdate.splice(i, 1);
      }
    }
  }
};

interface IUserFormProps {
  field: string;
  value: UserFormValueOptions;
  clonedUser?: IUser;
  clonedFormErrors?: IBootstrapFormErrors;
  clonedWarnings: IWarning[];
}

export const handleUserFormEdit = ({
  field,
  value,
  clonedUser,
  clonedFormErrors,
  clonedWarnings,
}: IUserFormProps) => {
  // SetField
  updateUserFormFields(field, value, clonedUser);
  // Clear value in formErrors
  updateFormErrors(field, clonedFormErrors);

  // Update list of warnings
  updateWarningsList(clonedWarnings, clonedFormErrors);
};

export const dataURLtoBlob = (uri: string) => {
  const splitUri = uri.split(',');
  const split = splitUri[0];
  const match = split.match(/:(.*?);/);
  const mimeType: string | undefined = match ? match[1] : undefined;
  const decodedString = atob(splitUri[1]);
  let index = decodedString.length;
  const unsignedArray = new Uint8Array(index);
  while (index--) {
    unsignedArray[index] = decodedString.charCodeAt(index);
  }

  return new Blob([unsignedArray], { type: mimeType });
};

export const processModuleAvailability = (course: ICourse) => {
  const { modulesInOrder, modules } = course;

  // Build module availability list
  const moduleAvailability: { module: IModule; isAvailable: boolean }[] = [];
  if (modules) {
    modules.forEach((m) => {
      const { timeReleasePeriod, isReleased, takeAnytime, isUnlocked, _id } = m;
      let isAvailable = !!(!timeReleasePeriod || isReleased);

      // If the parent course has the in-order-completion requirement and this module does not have
      // the "take anytime" flag set to true and the isUnlocked flag is not set to true, we must
      // determine its availability.
      if (modulesInOrder && !takeAnytime && !isUnlocked) {
        let found = false;
        let index = 0;

        // Loop through entire list of modules in course until reaching this module
        while (!found) {
          const priorModule = course.modules[index];
          const priorCompleted = priorModule.completedOverride || priorModule.completed;

          // When we reach this module in the loop, we don't need to keep checking further.
          // (If this module is the first, we don't need to make it unavailable no matter what.);
          if (priorModule._id === _id) {
            found = true;
            // If a module is reached on this looping that is not optional and not complete,
            // we know all the following modules (including this one) should not be available.
            // The exception being if this module does not meet the criteria at the beginning
            // of the If statement (Course is not in-order completion, take anytime flag is checked, or isUnlocked)
          } else if (!priorModule.isOptional && !priorCompleted) {
            isAvailable = false;
          }
          index += 1;
        }
      }

      moduleAvailability.push({ module: m, isAvailable });
    });
  }

  return moduleAvailability;
};

export const generateCLEString = (
  cleCredits: ICLECredit[],
  authoredBy?: string,
  isUnlocked?: boolean,
) => {
  if (!cleCredits || cleCredits.length === 0) {
    return;
  }

  let cleString = '';
  if (isUnlocked) {
    cleString = `Not available for you to receive CLE because this course is set to allow you to skip content. For learners who aren't permitted to skip content: `;
  }

  cleString += 'Currently available for CLE as follows: ';
  const alphabetizedCLEArray = cleCredits.sort((a, b) => (a.state > b.state ? 1 : -1));
  for (let i = 0; i < alphabetizedCLEArray.length; i++) {
    const toConcat = `${alphabetizedCLEArray[i].state} - ${alphabetizedCLEArray[i].credits}${
      i < alphabetizedCLEArray.length - 1 ? '; ' : '.'
    }`;
    cleString += toConcat;
  }

  if (authoredBy) {
    cleString += ` The content of this course was authored by ${authoredBy}`;
  }

  if (cleString[cleString.length - 1] !== '.') {
    cleString += '.';
  }

  return cleString;
};

export const returnUniqueValuesInColumn = (column: string, rows: Row<any>[]) => {
  const uniqueValues: IKey = {};

  rows.forEach((row) => {
    const valueInColumn = row.values[column];

    // If the value in the column is blank, add 'Blank' to the unique values list so that the user can filter by blank values.
    if (!valueInColumn || valueInColumn === '') {
      uniqueValues[BLANKS_FILTER] = '(Blanks)';
      return;
    }

    // If the value in the column is not blank and is not already in the unique values list, add it.
    if (valueInColumn && valueInColumn !== '' && !uniqueValues[valueInColumn.toLowerCase()]) {
      uniqueValues[valueInColumn.toLowerCase()] = valueInColumn;
    }
  });

  return uniqueValues;
};

interface ApplyFilterIProps {
  appliedFilters: IReportsFilterCategories;
  before?: boolean;
  checkedValues?: IReportFilterString[] | DateFilterType;
  column: string;
  displayString?: string;
  endDate?: Date;
  filterType?: string;
  notEqual?: boolean;
  quickFilterId?: string;
  startDate?: Date;
  setAppliedFilters: React.Dispatch<React.SetStateAction<IReportsFilterCategories>>;
  setShowDropdown?: React.Dispatch<React.SetStateAction<boolean>>;
}
export const applyFilter = ({
  appliedFilters,
  before,
  checkedValues,
  column,
  displayString,
  endDate,
  filterType,
  notEqual,
  quickFilterId,
  startDate,
  setAppliedFilters,
  setShowDropdown,
}: ApplyFilterIProps) => {
  const newAppliedFilters = cloneDeep(appliedFilters);

  const newValues: IReportsFilterValue = {
    displayName: displayString || '',
    stringValues: checkedValues,
    totalFilters: newAppliedFilters[column] ? newAppliedFilters[column].totalFilters : 0,
    startDate,
    endDate,
    filterType,
    notEqual,
    before,
    quickFilterId,
  };

  if (
    (!newValues.stringValues || (newValues.stringValues && newValues.stringValues?.length < 1)) &&
    filterTypeIsString(newValues.filterType) &&
    !newValues.startDate &&
    !newValues.endDate
  ) {
    newAppliedFilters[column].stringValues = [];
  } else {
    newAppliedFilters[column] = newValues;
  }
  setAppliedFilters(newAppliedFilters);

  if (setShowDropdown) {
    setShowDropdown(false);
  }
};

// TODO - move this function to @praktio/common as it is used in both lms-ui and learner-ui
export const capStringAtNumChars = (stringToCheck: string | undefined, maxChars: number) => {
  if (!stringToCheck) {
    return;
  }
  const lengthOfString = stringToCheck.length;
  const maxMinusThree = maxChars - 3;
  if (lengthOfString > maxChars) {
    return stringToCheck.slice(0, maxMinusThree - 1) + '...';
  }
  return stringToCheck;
};

export const useQuery = () => {
  const location = useLocation();
  return new URLSearchParams(location.search);
};

interface ISearchParamFieldProps {
  name: string;
  newValue?: string;
  remove?: boolean;
}

interface ISearchParamUpdateProps {
  fields: ISearchParamFieldProps[];
  searchParams: URLSearchParams;
  setSearchParams: (
    nextInit: URLSearchParamsInit,
    navigateOptions?:
      | {
          replace?: boolean | undefined;
          state?: any;
        }
      | undefined,
  ) => void;
}

export const updateSearchParams = ({
  fields,
  searchParams,
  setSearchParams,
}: ISearchParamUpdateProps) => {
  // TODO - add a "clearing all other search params" argument to make
  // the passed value the only present param in the search.
  const updatedSearchParams = new URLSearchParams(searchParams.toString());
  fields.forEach((field) => {
    if (field.remove) {
      updatedSearchParams.delete(field.name);
    } else if (field.newValue) {
      updatedSearchParams.set(field.name, field.newValue);
    } else {
      console.error('nothing changed in search params. exiting...');
    }
  });
  setSearchParams(updatedSearchParams.toString());
};

export const getDateString = (dateTime?: Date, options?: Intl.DateTimeFormatOptions) => {
  const date = dateTime ? new Date(dateTime) : new Date();
  return new Date(date.getTime() - TimezoneOffsetMs).toLocaleDateString(undefined, options);
};

export const getISODateString = (dateTime?: Date) => {
  const date = dateTime ? new Date(dateTime) : new Date();
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

export const getLocalTime = (dateTime?: Date, duration?: number, timeStyle?: string) => {
  if (!dateTime) {
    return '';
  }

  let dateValue = new Date(dateTime);
  if (duration) {
    dateValue = new Date(dateValue.getTime() + duration * 60 * 60 * 1000);
  }

  const localTime = dateValue.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  return localTime;
};

export const getLocalDateTime = (date?: string | Date | null) => {
  if (!date) {
    return;
  }

  if ((date as string).includes && !(date as string).includes('Z')) {
    return date;
  }

  const timeMs = new Date(date).getTime();

  // Drop excess data off ISO format, so go from this:
  // 2023-05-15T18:30:09.002Z
  // to this:
  // 2023-05-15T18:30
  // which is dropping 8 characters off the end
  const localValue = new Date(timeMs - TimezoneOffsetMs).toISOString().slice(0, -8);
  return localValue;
};

export const getMaintenanceMessage = (maintenance: IMaintenance) => {
  if (maintenance.start) {
    return `The Praktio platform will be unavailable from ${getLocalTime(
      maintenance.start,
    )} until  ${getLocalTime(maintenance.start, maintenance.duration)} on ${new Date(
      maintenance.start,
    ).toLocaleDateString()} for scheduled maintenance.`;
  }
};

/**
 * Rounds a number to a specified number of decimal places.
 * @param num
 * @param decimalPlaces
 * @returns A number rounded to the specified number of decimal places.
 * @example
 * roundNumber(1.2345, 2) // 1.23
 */
export const roundNumber = (num: number, decimalPlaces: number) => {
  return round(num, decimalPlaces);
};

/**
 * Calculates the percentage of a numerator over a denominator.
 * @param numerator
 * @param denominator
 * @param decimalPlaces
 * @returns The percentage of the numerator over the denominator rounded to the specified number of decimal places.
 */
export const calculatePercentage = (
  numerator: number,
  denominator: number,
  decimalPlaces = 2,
): number => {
  if (denominator === 0) return 0;
  return Number(((numerator / denominator) * 100).toFixed(decimalPlaces));
};

type RatioFormatter = (numerator: number) => string;

/**
 * Creates a ratio formatter for a given denominator and label.
 * @param denominator
 * @param label
 * @returns A ratio formatter function that formats a numerator as a ratio with the given denominator and label.
 */
export const createRatioFormatter = (denominator: number, label: string): RatioFormatter => {
  return (numerator: number): string => `${numerator}/${denominator} ${label}`;
};

// ===========================================================================
// Report Table
// ===========================================================================

export const dateFilterTypeIsRange = (dateFilterType: string) => dateFilterType === 'range';
export const filterTypeIsDate = (filterType: string | undefined) => filterType === 'date';
export const filterTypeIsString = (filterType: string | undefined) => filterType === 'string';

export const getUniqueStringValues = (uniqueValues: IKey) => {
  return Object.keys(uniqueValues).map((key) => ({
    displayString: key,
    comparisonValue: key,
  }));
};

export const formatDateFilterTypeForDisplay = (filterType: DateFilterType) => {
  if (filterType === DateFilterType.blankOnly) {
    return 'Blanks';
  }

  return filterType
    .split(/(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Check if current selected teams or deselected courses have changed compared to active teams or active deselected courses
 * @param activeTeams - The active teams stored in redux state
 * @param activeDeselectedCourses - The active deselected courses stored in redux state
 * @param selectedTeams - The current selected teams
 * @param deselectedCourses - The current deselected courses
 * @returns boolean - True if current selected teams or deselected courses have changed
 */
export const teamsOrDeselectedCoursesHaveChanged = (
  activeTeams: string[],
  activeDeselectedCourses: string[],
  selectedTeams: string[],
  deselectedCourses: string[],
) => {
  return !arraysAreEqual(
    [...activeTeams, ...activeDeselectedCourses],
    [...selectedTeams, ...deselectedCourses],
  );
};

/**
 * Generate a file name for an Excel report.
 * @param fileName
 * @param date - Optional date and time to use in the file name
 * @returns string - The generated file name in the format "[fileName] [date] [time].xlsx"
 * @example generateReportXlsxFileName('Praktio Summary Report') // "Praktio Summary Report 8-27-2024 1200PM.xlsx"
 */
export const generateReportXlsxFileName = (fileName: string, date: Date): string => {
  const dateLocal = date.toLocaleDateString().replaceAll('/', '-');

  const time = date
    .toLocaleTimeString(undefined, { timeStyle: 'short' })
    .replaceAll(':', '')
    .replaceAll(' ', '');

  return `${fileName} ${dateLocal} ${time}.xlsx`;
};

/**
 * Check if two arrays are equal after sorting them.
 * @param array1
 * @param array2
 * @returns boolean - True if the two arrays are equal after sorting them
 */
export function arraysAreEqual<T>(array1: T[], array2: T[]): boolean {
  return isEqual(sortBy(array1), sortBy(array2));
}

export const getCookieConsent = () => (window as any).getCkyConsent?.();

export const checkAnalyticsCookiesAccepted = () => {
  const cookieConsent = getCookieConsent();
  return cookieConsent?.isUserActionCompleted && cookieConsent?.categories?.analytics;
};

export const openHubSpotChat = () => {
  const hubSpotWrapper = document.getElementById('hubspot-messages-iframe-container');
  hubSpotWrapper?.classList?.add('chat-is-open');
  setTimeout(() => {
    (window as IWindow)?.HubSpotConversations?.widget?.open();
  }, 100);
};

export const closeHubSpotChat = () => {
  (window as IWindow)?.HubSpotConversations?.widget?.close();
  const hubSpotWrapper = document.getElementById('hubspot-messages-iframe-container');
  hubSpotWrapper?.classList?.remove('chat-is-open');
};
